<script>
import { inject, ref } from 'vue'

import { TIME_VARIABLES } from '../ea-filter/_ea-filter-kind-data'

export default {
  name: 'EaDatetimepicker',
  inheritAttrs: false,
  emits: [
    'update:modelValue',
    'onDayCreate',
    'onValueUpdate',
    'onReady',
    'onYearChange',
    'onMonthChange',
    'onClose',
    'onOpen',
    'onChange',
  ],
  props: {
    errorsTeleport: { type: String, default: null },
    initialVisibleDate: {
      type: Object,
      default: null,
      validator(v) {
        if (!v) return true
        return dayjs.isDayjs(v)
      },
    },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    skipDirtyCheck: { type: Boolean, default: false },
    label: { type: String, default: null },
    name: { type: String, required: true },
    modelValue: { type: String || Array },
    timeZone: {
      type: String,
      default: null,
    },
    inputSize: {
      type: String,
      default: null,
      validator(val) {
        return !val || ['sm', 'lg'].includes(val)
      },
    },
    inputClasses: {
      type: [String, Array, Object],
      default: null,
    },
    placeholder: { type: String, default: '' },
    hint: { type: String, default: null },
    inputAppend: { type: String, default: null },
    inputPrepend: { type: String, default: null },
    hideErrors: { type: Boolean, default: false },
    hideInvalidIcon: { type: Boolean, default: false },
    config: {
      type: Object,
      default: () => ({}),
    },
    validators: { type: Object, default: null },
    additionalErrors: {
      type: Array,
      default: () => [],
      validator(val) {
        return !val.find(item => item.constructor != String)
      },
    },
  },
  setup(props, { expose }) {
    const flatpickCmpRef = ref(null)
    const wasOpened = ref(false)
    const inModal = inject('inModal')
    const modalEl = inject('modalEl', null)

    expose({
      focus: () => {
        flatpickCmpRef.value?.fpInput()?.focus?.()
      },
    })

    return {
      flatpickCmpRef,
      wasOpened,
      inModal,
      modalEl,
    }
  },
  computed: {
    clearIconVisible() {
      return (
        this.clearable &&
        this.modelValue?.length &&
        !Object.keys(TIME_VARIABLES).includes((this.modelValue || '').toString())
      )
    },
    passedSlots() {
      return Object.keys(this.$slots).filter(slot =>
        ['label-addon', 'input-append', 'input-prepend', 'input-prepend-icon'].includes(slot),
      )
    },
    properTimeZone() {
      return this.timeZone || this.$store.state.session.timeZone
    },
    sizeClass() {
      return this.inputSize ? `form-control-${this.inputSize}` : ''
    },
    flatpickrConfig() {
      const now = this.operationalNowDate()
      const result = Object.assign(
        {
          time_24hr: true,
          enableTime: true,
          altFormat: 'd/m/Y H:i',
          altInput: true,
          weekNumbers: false,
          allowInput: true,
          defaultHour: 0,
        },
        this.config,
        {
          now,
          static: false,
          wrap: false,
          nextArrow:
            '<div class="btn btn-sm px-2 btn-primary"><i class="fas fa-chevron-right"></div>',
          prevArrow:
            '<div class="btn btn-sm px-2 btn-primary"><i class="fas fa-chevron-left"></div>',
          dateFormat: 'Y-m-d H:i:00',
          appendTo: document.getElementById('fixed-elements-portal'),
        },
      )
      if (this.inModal && !result.position?.length) {
        result.position = 'bottom'
      }
      return result
    },
    flatpickrModelValue: {
      get() {
        let result = null
        if (Object.keys(TIME_VARIABLES).includes((this.modelValue || '').toString())) {
          return result
        } else if (this.modelValue?.constructor == Array) {
          result = [
            dayjs
              .tz(
                `${dayjs(this.modelValue[0])
                  .tz(this.properTimeZone)
                  .format('YYYY-MM-DD HH:mm:00')}`,
                dayjs.tz.guess(),
              )
              .format('YYYY-MM-DD HH:mm:00'),
            dayjs
              .tz(
                `${dayjs(this.modelValue[1])
                  .tz(this.properTimeZone)
                  .format('YYYY-MM-DD HH:mm:00')}`,
                dayjs.tz.guess(),
              )
              .format('YYYY-MM-DD HH:mm:00'),
          ]
        } else if (this.modelValue?.length) {
          result = dayjs
            .tz(
              `${dayjs(this.modelValue).tz(this.properTimeZone).format('YYYY-MM-DD HH:mm:00')}`,
              dayjs.tz.guess(),
            )
            .format('YYYY-MM-DD HH:mm:00')
        }
        return result
      },
      set(newVal) {
        if (newVal == this.flatpickrModelValue) {
          this.$emit('update:modelValue', this.modelValue)
          return
        } else if (
          this.flatpickrModelValue !== null &&
          this.flatpickrModelValue.constructor == Array &&
          newVal.split(' to ')[0] == this.flatpickrModelValue[0] &&
          newVal.split(' to ')[1] == this.flatpickrModelValue[1]
        ) {
          this.$emit('update:modelValue', this.modelValue)
          return
        }

        let result = null
        if (/^.+ to .+$/.test(newVal)) {
          const [from, to] = newVal.split(' to ')

          result = [
            dayjs.tz(from, this.properTimeZone).utc().format(),
            dayjs.tz(to, this.properTimeZone).utc().format(),
          ]
        } else if (newVal?.length) {
          result = dayjs.tz(newVal, this.properTimeZone).utc().format()
        }
        this.$emit('update:modelValue', result)
      },
    },
  },
  watch: {
    properTimeZone() {
      this.$nextTick(() => {
        this.syncFlatpickrOperationalNow()
      })
    },
  },
  unmounted() {
    this.removeModalListeners()
  },
  methods: {
    operationalNowDate() {
      const operationalNow = dayjs().tz(this.properTimeZone)
      return new Date(
        operationalNow.year(),
        operationalNow.month(),
        operationalNow.date(),
        operationalNow.hour(),
        operationalNow.minute(),
        operationalNow.second(),
      )
    },
    syncFlatpickrOperationalNow() {
      const fp = this.flatpickCmpRef?.fp
      if (!fp) return

      const now = this.operationalNowDate()
      // fp.set({ now }) updates config.now but not fp.now; the "today" marker uses fp.now.
      fp.config.now = now
      fp.now = now
      fp.redraw()
    },
    closePicker() {
      if (this.flatpickCmpRef?.fp?.isOpen) {
        this.flatpickCmpRef.fp.close()
      }
    },
    runModalListeners() {
      if (this.inModal) {
        this.$eventHub.$on('windowResize', this.closePicker)
        this.modalEl?.addEventListener('scroll', this.closePicker)
        this.flatpickCmpRef?.fp?.calendarContainer?.addEventListener('mousedown', this.preventFn)
      }
    },
    removeModalListeners() {
      if (this.inModal) {
        this.$eventHub.$off('windowResize', this.closePicker)
        this.modalEl?.removeEventListener('scroll', this.closePicker)
        this.flatpickCmpRef?.fp?.calendarContainer?.removeEventListener('mousedown', this.preventFn)
      }
    },
    onOpen(e) {
      if (!this.wasOpened && this.initialVisibleDate && this.flatpickCmpRef?.fp) {
        this.wasOpened = true
        this.flatpickCmpRef.fp._initialDate = this.initialVisibleDate.toDate()
        if (!this.modelValue) {
          this.flatpickCmpRef.fp.setDate(null)
        }
      }
      this.runModalListeners()
      this.$emit('onOpen', e)
    },
    preventFn(e) {
      e.stopPropagation()
      e.preventDefault()
    },
    onClose(e) {
      this.removeModalListeners()
      this.$emit('onClose', e)
    },
  },
}
</script>

<template lang="pug">
form-control-container(
  ref="fcc"
  :additional-errors="additionalErrors"
  :errorsTeleport="errorsTeleport"
  :hideErrors="hideErrors"
  :hideInvalidIcon="hideInvalidIcon"
  :hint="hint"
  :input-size="inputSize"
  :inputAppend="inputAppend"
  :inputPrepend="inputPrepend"
  :label="label"
  :name="name"
  :skipDirtyCheck="skipDirtyCheck"
  :validators="validators"
  :value="modelValue"
)
  template(v-for="slotName in passedSlots" #[slotName]="item")
    slot(v-bind="item" :name="slotName")

  template(#default)
    .input-addon-icon-btn(
      v-if="clearIconVisible && !disabled"
      @click="$emit('update:modelValue', null)"
      v-tooltip.options="{ title: 'Clear', placement: 'left' }"
    )
      i.bi-x(:name="name")
    flat-pickr.form-control(
      ref="flatpickCmpRef"
      :class="[sizeClass, inputClasses]"
      :config="flatpickrConfig"
      :disabled="disabled"
      :name="name"
      :placeholder="placeholder"
      v-model="flatpickrModelValue"
      @mousedown.prevent.stop
      @mouseup.prevent.stop
      @onChange="$emit('onChange', $event)"
      @onClose="onClose($event)"
      @onDayCreate="$emit('onDayCreate', $event)"
      @onMonthChange="$emit('onMonthChange', $event)"
      @onOpen="onOpen($event)"
      @onReady="$emit('onReady', $event)"
      @onValueUpdate="$emit('onValueUpdate', $event)"
      @onYearChange="$emit('onYearChange', $event)"
    )
</template>
